import { BarsOutlined, InfoCircleTwoTone } from "@ant-design/icons";
import type { FormInstance, FormListFieldData } from "antd";
import { Button, Checkbox, Form, Input, Select, Space, Tooltip } from "antd";
import SchemaClassifiersFieldProperty from "components/atoms/SchemaClassifiersFieldProperty";
import Segment from "components/atoms/Segment";
import { useRef, useState } from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";
import StaticPickListModal from "../StaticPickListModal";

const { Option } = Select;

interface IPicklistFieldPropertiesProps {
    formRef: React.RefObject<FormInstance>;
    selectedFieldRow: FormListFieldData;
    readOnly?: boolean;
    getBusinessAreaDataForBusinessAreaResult?: IResultState;
    businessAreaDataList?: IBusinessAreaData[];
    getFormErrors: (name: string) => Object;
    onDataDomainChange: (dataDomainId: string) => void;
    onPicklistTypeChange: (picklistType: string) => void;
}

const PicklistFieldProperties = ({
    formRef,
    selectedFieldRow,
    readOnly,
    getBusinessAreaDataForBusinessAreaResult,
    businessAreaDataList,
    getFormErrors,
    onDataDomainChange,
    onPicklistTypeChange,
}: IPicklistFieldPropertiesProps) => {
    const [showPicklistForm, setShowPicklistForm] = useState(false);
    const previousStaticPicklist = useRef(formRef.current?.getFieldValue(["fields", selectedFieldRow.name, "staticPicklist"]) || []);

    const onDataDomainValueChange = (dataDomainId: string, fieldKey: any) => {
        onDataDomainChange(dataDomainId);
        const field = formRef.current?.getFieldValue(fieldKey);
        const currentDataDomain = businessAreaDataList?.find((item) => item.dataDomainId === dataDomainId);
        if (currentDataDomain) {
            const dataDomainClassifiers = currentDataDomain.classifiers;
            if (dataDomainClassifiers && dataDomainClassifiers.length > 0) {
                const existingFieldClassifiers = new Set<string>();
                const fieldClassifiers = field.classifiers ? [...field.classifiers] : [];
                if (field.classifiers && field.classifiers.length > 0) {
                    for (let classifier of field.classifiers) {
                        existingFieldClassifiers.add(classifier.tagId);
                    }
                }

                for (let classifier of dataDomainClassifiers) {
                    if (existingFieldClassifiers.has(classifier.tagId) === false) {
                        fieldClassifiers.push({ ...classifier });
                    }
                }
                if ((field.classifiers || []).length !== fieldClassifiers.length) {
                    const fields = formRef.current?.getFieldValue([fieldKey[0]]);
                    if (fields && fields.length > 0) {
                        const updatedFields = [...fields];
                        updatedFields[fieldKey[1]] = { ...field, classifiers: fieldClassifiers };
                        formRef.current?.setFieldValue([fieldKey[0]], updatedFields);
                    }
                }
            }
        }
    };

    return (
        <>
            <Form.List name={["fields", selectedFieldRow.name, "staticPicklist"]}>
                {(fields, { add, remove }, { errors }) => {
                    return (
                        <>
                            {/* Explicitly list hidden required fields to ensure AntD form does not drop them when unmounted */}
                            {fields.map((field) => (
                                <div key={field.key} style={{ display: "none" }}>
                                    <Form.Item hidden name={[field.name, "code"]}>
                                        <Input />
                                    </Form.Item>
                                    <Form.Item hidden name={[field.name, "description"]}>
                                        <Input />
                                    </Form.Item>
                                </div>
                            ))}
                            <StaticPickListModal
                                formRef={formRef}
                                fields={fields}
                                fieldErrors={errors}
                                readOnly={readOnly}
                                visible={showPicklistForm}
                                maxLength={25}
                                onAddRow={add}
                                onRemoveRow={remove}
                                onCreate={() => {
                                    formRef.current
                                        ?.validateFields()
                                        .then((values) => {
                                            setShowPicklistForm(false);
                                            onPicklistTypeChange("static");
                                        })
                                        .catch((errorInfo) => {
                                            console.error(errorInfo);
                                        });
                                }}
                                onCancel={() => {
                                    setShowPicklistForm(false);
                                    formRef.current?.setFieldValue(["fields", selectedFieldRow.name, "staticPicklist"], previousStaticPicklist.current || []);
                                    onPicklistTypeChange("static");
                                }}
                            ></StaticPickListModal>
                        </>
                    );
                }}
            </Form.List>
            <Segment.Group>
                <Segment className="form-background">
                    <Form.Item label="Picklist Type" name={["fields", selectedFieldRow.name, "picklistType"]} initialValue="dynamic">
                        <Select {...(() => (readOnly ? { open: false } : {}))()} onChange={onPicklistTypeChange}>
                            <Option key={"dynamic"} value={"dynamic"}>
                                Dynamic
                            </Option>
                            <Option key={"static"} value={"static"}>
                                Static
                            </Option>
                        </Select>
                    </Form.Item>
                </Segment>
                <Segment className="form-background">
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues["fields"][selectedFieldRow.name] &&
                            currentValues["fields"][selectedFieldRow.name] &&
                            prevValues["fields"][selectedFieldRow.name]["picklistType"] !== currentValues["fields"][selectedFieldRow.name]["picklistType"]
                        }
                    >
                        {({ getFieldValue }) =>
                            getFieldValue(["fields", selectedFieldRow.name, "picklistType"]) === "dynamic" || !getFieldValue(["fields", selectedFieldRow.name, "picklistType"]) ? (
                                <Form.Item
                                    label="Domain Data"
                                    name={["fields", selectedFieldRow.name, "dataDomainId"]}
                                    rules={[{ required: true, message: "Please select domain data" }]}
                                    {...getFormErrors(`fields.${selectedFieldRow.name}.dataDomainId`)}
                                >
                                    <Select
                                        loading={getBusinessAreaDataForBusinessAreaResult?.inProgress}
                                        {...(() => (readOnly ? { open: false } : {}))()}
                                        onChange={(value) => onDataDomainValueChange(value, ["fields", selectedFieldRow.name])}
                                    >
                                        {businessAreaDataList?.map((businessAreaData) => (
                                            <Option key={businessAreaData.dataDomainId} value={businessAreaData.dataDomainId}>
                                                {businessAreaData.domainName}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            ) : (
                                <Form.Item shouldUpdate={true} label="Picklist Values">
                                    {({ getFieldValue }) => {
                                        const picklistValues: IDomainDataResultItem[] = getFieldValue(["fields", selectedFieldRow.name, "staticPicklist"]);
                                        const picklistValuesString = (picklistValues && picklistValues.map((item) => item && item.code).join(", ")) || "";
                                        return (
                                            <Space>
                                                <Input value={picklistValuesString} readOnly={true} onClick={() => setShowPicklistForm(true)} />
                                                <Button
                                                    onClick={() => {
                                                        setShowPicklistForm(true);
                                                    }}
                                                    disabled={readOnly}
                                                    icon={<BarsOutlined />}
                                                    variant="filled"
                                                />
                                            </Space>
                                        );
                                    }}
                                </Form.Item>
                            )
                        }
                    </Form.Item>
                </Segment>
                <Segment className="form-background">
                    <SchemaClassifiersFieldProperty selectedFieldRow={selectedFieldRow} readOnly={readOnly} getFormErrors={getFormErrors}></SchemaClassifiersFieldProperty>
                </Segment>
                <Segment className="form-background">
                    <Form.Item label="Multi Value" name={["fields", selectedFieldRow.name, "isMultivalue"]} valuePropName="checked">
                        <Checkbox disabled={readOnly} data-testid={CypressTestIds.SCHEMA_MULTIVALUE_PROPERTY_CHECKBOX}></Checkbox>
                    </Form.Item>
                </Segment>
                <Segment className="form-background">
                    <Form.Item label="PII" name={["fields", selectedFieldRow.name, "isPII"]} valuePropName="checked">
                        <Checkbox disabled={readOnly} data-testid={CypressTestIds.SCHEMA_PII_PROPERTY_CHECKBOX}>
                            <Tooltip title="Flag this field as Personally Identifiable Information or Sensitive Data">
                                <InfoCircleTwoTone />
                            </Tooltip>
                        </Checkbox>
                    </Form.Item>
                </Segment>
            </Segment.Group>
        </>
    );
};

export default PicklistFieldProperties;
